import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Zap, CheckCircle2, XCircle, AlertTriangle, ArrowRight } from "lucide-react";

const EMP_TYPES = ["salaried", "self_employed", "business_owner", "professional", "trader", "contractor", "farmer", "transport_operator", "commission_agent", "gig_worker", "other"];

export default function CheckEligibility() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", mobile: "", pan: "", city: "", state: "",
    employment_type: "salaried", monthly_income: "", existing_emi: "",
    desired_loan_amount: "", vehicle_price: "", down_payment: "",
    cibil_consent: false, referral_code: "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const check = async () => {
    if (!form.cibil_consent) { toast.error("Please provide CIBIL consent to check eligibility"); return; }
    if (!form.monthly_income) { toast.error("Monthly income is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      ["monthly_income", "existing_emi", "desired_loan_amount", "vehicle_price", "down_payment"].forEach(k => { payload[k] = Number(payload[k]) || 0; });
      const { data } = await api.post("/eligibility/quick", payload);
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Eligibility check failed"); }
    finally { setBusy(false); }
  };

  const STYLES = {
    "Eligible": { cls: "bg-emerald-50 border-emerald-300 text-emerald-900", Icon: CheckCircle2 },
    "Refer": { cls: "bg-amber-50 border-amber-300 text-amber-900", Icon: AlertTriangle },
    "Not Eligible": { cls: "bg-red-50 border-red-300 text-red-900", Icon: XCircle },
  };

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12" data-testid="check-eligibility-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">Instant decision · No impact on your credit score</div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-slate-900">Check your eligibility in 30 seconds</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">Salaried, self-employed, business owner or farmer — our cash-flow based engine estimates your eligible used car loan instantly against Red Fort Capital Finance credit policy.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Full name</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="qe-name" className="mt-1" /></div>
            <div><Label className="text-xs">Mobile</Label><Input value={form.mobile} onChange={e => set("mobile", e.target.value)} data-testid="qe-mobile" className="mt-1" /></div>
            <div><Label className="text-xs">PAN</Label><Input value={form.pan} onChange={e => set("pan", e.target.value.toUpperCase())} data-testid="qe-pan" className="mt-1" /></div>
            <div><Label className="text-xs">City</Label><Input value={form.city} onChange={e => set("city", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">State</Label><Input value={form.state} onChange={e => set("state", e.target.value)} className="mt-1" /></div>
            <div className="col-span-2">
              <Label className="text-xs">Employment type</Label>
              <Select value={form.employment_type} onValueChange={v => set("employment_type", v)}>
                <SelectTrigger className="mt-1" data-testid="qe-employment"><SelectValue /></SelectTrigger>
                <SelectContent>{EMP_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Monthly income (₹)</Label><Input type="number" value={form.monthly_income} onChange={e => set("monthly_income", e.target.value)} data-testid="qe-income" className="mt-1" /></div>
            <div><Label className="text-xs">Existing EMIs (₹/mo)</Label><Input type="number" value={form.existing_emi} onChange={e => set("existing_emi", e.target.value)} data-testid="qe-emi" className="mt-1" /></div>
            <div><Label className="text-xs">Vehicle price (₹)</Label><Input type="number" value={form.vehicle_price} onChange={e => set("vehicle_price", e.target.value)} data-testid="qe-vehicle-price" className="mt-1" /></div>
            <div><Label className="text-xs">Desired loan (₹)</Label><Input type="number" value={form.desired_loan_amount} onChange={e => set("desired_loan_amount", e.target.value)} data-testid="qe-desired-loan" className="mt-1" /></div>
            <div><Label className="text-xs">Down payment available (₹)</Label><Input type="number" value={form.down_payment} onChange={e => set("down_payment", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Dealer / DSA referral code</Label><Input value={form.referral_code} onChange={e => set("referral_code", e.target.value)} className="mt-1" /></div>
            <div className="col-span-2 flex items-start gap-2 mt-1">
              <Checkbox checked={form.cibil_consent} onCheckedChange={v => set("cibil_consent", !!v)} data-testid="qe-consent" className="mt-0.5" />
              <span className="text-xs text-slate-600">I consent to CarFin India / Red Fort Capital Finance NBFC accessing my credit bureau (CIBIL) information for this eligibility check, per the <a href="/consent-policy" className="text-teal-700 underline">Consent Policy</a>.</span>
            </div>
          </div>
          <Button onClick={check} disabled={busy} className="bg-teal-600 hover:bg-teal-700 w-full mt-4" data-testid="qe-check-btn">
            <Zap size={16} className="mr-1" /> Check eligibility
          </Button>
        </Card>

        <Card className="p-6 border-slate-200" data-testid="qe-result">
          {!result ? (
            <div className="h-full flex flex-col justify-center items-center text-center text-slate-400 py-12">
              <Zap size={36} className="mb-3" />
              <div className="text-sm">Your instant eligibility result will appear here.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {(() => { const S = STYLES[result.verdict]; const I = S.Icon; return (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-xl font-semibold ${S.cls}`} data-testid="qe-verdict">
                  <I size={22} /> {result.verdict}
                </div>
              ); })()}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat l="Estimated eligible loan" v={formatINR(result.estimated_eligible_loan)} big />
                <Stat l="Estimated EMI" v={`${formatINR(result.estimated_emi)}/mo`} big />
                <Stat l="EMI capacity" v={`${formatINR(result.estimated_emi_capacity)}/mo`} />
                <Stat l="Required down payment" v={formatINR(result.required_down_payment)} />
                <Stat l="Indicative ROI" v={`${result.indicative_roi_range[0]}–${result.indicative_roi_range[1]}% p.a.`} />
                <Stat l="Tenure" v={`up to ${result.tenure_months} months`} />
              </div>
              {result.reason_codes?.length > 0 && (
                <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                  <ul className="list-disc pl-5 space-y-1">{result.reason_codes.map(r => <li key={r}>{r}</li>)}</ul>
                </div>
              )}
              <div className="text-sm">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Next steps</div>
                <ul className="space-y-1">{result.next_steps.map(s => <li key={s} className="flex items-center gap-2"><ArrowRight size={13} className="text-teal-600" /> {s}</li>)}</ul>
              </div>
              <Button onClick={() => navigate("/apply")} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="qe-apply-btn">
                Continue to full application <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          )}
        </Card>
      </div>
      <p className="text-xs text-slate-400 mt-6">Indicative only. Final terms are subject to CarDr vehicle diagnostics, valuation, document verification and Red Fort Capital Finance NBFC underwriting approval.</p>
    </div>
  );
}

function Stat({ l, v, big }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div>
      <div className={`font-mono font-semibold text-slate-900 ${big ? "text-xl" : ""}`}>{v}</div>
    </div>
  );
}
